import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, themeQuartz } from "ag-grid-community";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { FileSpreadsheet, Download, PauseCircle, Loader2, Printer, FileText, FileDown, Undo2, Redo2, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useConferma } from "../components/ConfirmProvider";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte.
import { formatDate, formatDateTime } from "../lib/format";

const MONTH_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MONTH_FULL = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

// Telefono leggibile: +393273711316 -> +39 327 371 1316 (e cliccabile per chiamare)
const formatPhone = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  const cifre = s.replace(/\D/g, "");
  if (cifre.startsWith("39") && cifre.length >= 11) {
    const n = cifre.slice(2);
    return `+39 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`.trim();
  }
  if (cifre.length === 10) return `${cifre.slice(0, 3)} ${cifre.slice(3, 6)} ${cifre.slice(6)}`;
  return s;
};

const euroFormatter = (params) => {
  if (params.value == null) return "";
  return Number(params.value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const dataFormatter = (params) => formatDate(params.value, "");

const euroParser = (params) => {
  const val = params.newValue?.toString().replace(/[^\d.,-]/g, "").replace(",", ".");
  return parseFloat(val) || 0;
};

// v3.9.15 — Etichette dei campi per il pannello "Cronologia"
const FIELD_LABELS = {
  note: "Note",
  comunicato: "Comunicato",
  compagnia: "Compagnia",
  ramo: "Ramo",
  targa: "Targa",
  importo_netto: "Importo netto",
  totale: "Totale",
};

// Valore di una voce di cronologia, leggibile (numeri in formato italiano)
const fmtValoreStorico = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (field === "importo_netto" || field === "totale") {
    const n = Number(v);
    if (!Number.isNaN(n)) {
      return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  return String(v);
};

// Data/ora della modifica: 29/09/2026 — usa l'helper unico (gg/mm/aaaa HH:MM).
const fmtQuandoStorico = (ts) => formatDateTime(ts);

// AG Grid theme customized
const gridTheme = themeQuartz.withParams({
  headerHeight: 36,
  rowHeight: 34,
  fontSize: 13,
  headerFontSize: 12,
  spacing: 4,
});

function MonthGrid({ rows, totals, onCellEdit, suppressRef, gridRef, onRigaSelezionata, filtro = "" }) {
  const conferma = useConferma();

  const columnDefs = useMemo(() => [
    {
      headerName: "SCA",
      field: "sca",
      width: 65,
      editable: false,
      cellStyle: { fontFamily: "monospace", textAlign: "center" },
    },
    {
      headerName: "RAT",
      field: "rat",
      width: 55,
      editable: false,
      cellStyle: (params) => ({
        textAlign: "center",
        fontWeight: "bold",
        fontSize: params.value === "VAR" ? "10px" : undefined,
        color:
          params.value === "VAR"
            ? "#7c3aed"
            : params.value === "S"
              ? "#d97706"
              : "#6b7280",
      }),
    },
    {
      headerName: "CONTRAENTE",
      field: "contraente",
      // v3.9.14 - piu' spazio: il nome puo' essere lungo e a destra c'e' l'etichetta
      minWidth: 320,
      flex: 2,
      editable: false,
      // fermo a sinistra: scorrendo a destra non si perde di vista di chi e' la riga
      pinned: "left",
      cellStyle: { fontWeight: "500" },
      // v3.9.14 - il nome sta a sinistra, le etichette in fondo a DESTRA nella cella
      cellRenderer: (p) => {
        if (!p.data || p.data._isTotalRow) return p.value;
        const etichette = [];
        if (p.data.renewed) {
          etichette.push(
            <span key="rinn" className="text-[9px] font-bold text-green-700 border border-green-600 rounded px-1 whitespace-nowrap">
              RINNOVATA
            </span>
          );
        }
        if (p.data.not_renewed) {
          etichette.push(
            <span key="nonrinn" className="text-[9px] font-bold text-red-700 border border-red-500 rounded px-1 whitespace-nowrap">
              NON RINNOVATA
            </span>
          );
        }
        if (p.data.rata_pagata) {
          etichette.push(
            <span
              key="rata"
              className="text-[9px] font-bold text-emerald-700 border border-emerald-600 rounded px-1 whitespace-nowrap"
              title={p.data.rata_pagata_data ? `Rata incassata il ${formatDate(p.data.rata_pagata_data)}` : "Rata incassata"}
            >
              RATA PAGATA
            </span>
          );
        }
        return (
          <span className="flex items-center justify-between gap-2 w-full">
            <span className="truncate">{p.value}</span>
            {etichette.length ? (
              <span className="flex items-center gap-1 shrink-0">{etichette}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      headerName: "RAMO",
      field: "ramo",
      width: 100,
      editable: true,
    },
    {
      headerName: "IMP. NETTO",
      field: "importo_netto",
      width: 115,
      editable: true,
      cellStyle: { textAlign: "right", fontFamily: "monospace" },
      valueFormatter: euroFormatter,
      valueParser: euroParser,
    },
    {
      headerName: "COMPETENZE",
      field: "competenze",
      width: 110,
      editable: false,
      cellStyle: { textAlign: "right", fontFamily: "monospace", color: "#6b7280" },
      valueFormatter: euroFormatter,
    },
    {
      headerName: "TOTALE",
      field: "totale",
      width: 110,
      editable: true,
      cellStyle: { textAlign: "right", fontFamily: "monospace", fontWeight: "600" },
      valueFormatter: euroFormatter,
      valueParser: euroParser,
    },
    {
      headerName: "COMPAGNIA",
      field: "compagnia",
      width: 130,
      editable: true,
    },
    {
      headerName: "TARGA",
      field: "targa",
      width: 110,
      editable: true,
      cellStyle: { fontFamily: "monospace", textTransform: "uppercase" },
    },
    {
      headerName: "TELEFONO",
      field: "telefono",
      width: 140,
      editable: false,
      cellStyle: { color: "#6b7280" },
      cellRenderer: (params) => {
        if (!params.value || params.node?.rowPinned) return params.value || "";
        const perChiamare = String(params.value).replace(/[^\d+]/g, "");
        return (
          <a
            href={`tel:${perChiamare}`}
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
            title={`Chiama ${formatPhone(params.value)}`}
          >
            {formatPhone(params.value)}
          </a>
        );
      },
    },
    {
      // Era "RINNOVO": in realta' e' il campo libero dove il titolare annota
      // lo stato del rinnovo ("AVVISATO", "da dire", "?"). Il nome giusto e' NOTE.
      headerName: "NOTE",
      field: "note",
      minWidth: 200,
      flex: 1,
      editable: true,
      cellStyle: { fontSize: "12px" },
    },
    {
      // tolta dallo schermo: il campo e' a zero su tutte le polizze.
      // Resta nel database e si puo' riaccendere togliendo questa riga.
      hide: true,
      headerName: "",
      field: "comunicato",
      width: 50,
      editable: true,
      cellDataType: "boolean",
      cellRenderer: (params) => {
        if (params.node.rowPinned) return null;
        return params.value ? "✓" : "";
      },
      cellStyle: (params) => ({
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "bold",
        color: params.value ? "#16a34a" : "#d1d5db",
        cursor: "pointer",
      }),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    suppressMovable: false,
    // passando il mouse su una cella tagliata si legge il contenuto intero
    tooltipValueGetter: (params) => (params.value == null ? "" : String(params.value)),
  }), []);

  // v3.9.15 — Selezione di UNA riga (niente caselle): serve al pannello
  // "Cronologia" per mostrare le modifiche della polizza selezionata.
  // In AG Grid v35 la selezione si configura con un oggetto (non piu' "single").
  const rowSelection = useMemo(() => ({
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: true,
    isRowSelectable: (params) => !params.data?._isTotalRow,
  }), []);

  const onSelectionChanged = useCallback((event) => {
    if (!onRigaSelezionata) return;
    const selezionate = event.api.getSelectedRows?.() || [];
    const riga = selezionate.find((r) => r && !r._isTotalRow) || null;
    onRigaSelezionata(riga);
  }, [onRigaSelezionata]);

  // v3.9.12 — i campi economici non si salvano con un click: serve conferma, e
  // se il salvataggio fallisce la griglia torna al valore vero (rollback).
  const FINANCIAL_FIELDS = {
    importo_netto: {
      label: "Importo netto",
      effetto: "modifica il PREMIO della polizza",
    },
    totale: {
      label: "Totale",
      effetto: "modifica la CONSULENZA della polizza",
    },
  };
  const suppressChangeRef = suppressRef || { current: false };

  const onCellValueChanged = useCallback(async (event) => {
    if (suppressChangeRef.current) return;
    const { colDef, data, newValue, oldValue, node } = event;
    if (data?._isTotalRow) return;
    // Una variazione non si corregge dalla griglia: si annulla e si rifà
    if (data?.is_adjustment) {
      suppressChangeRef.current = true;
      node.setDataValue(colDef.field, oldValue);
      suppressChangeRef.current = false;
      return;
    }
    const field = colDef.field;

    // nessun cambiamento reale (es. dopo un rollback): non fare nulla
    if (newValue === oldValue) return;

    // campi economici: conferma esplicita prima di salvare
    if (FINANCIAL_FIELDS[field]) {
      const info = FINANCIAL_FIELDS[field];
      const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      const ok = await conferma({
        titolo: `Modificare "${info.label}" di ${data.contraente || "questa polizza"}?`,
        descrizione:
          `Da: ${fmt(oldValue)}\nA: ${fmt(newValue)}\n\n` +
          `Questa modifica ${info.effetto} e viene salvata subito.`,
      });
      if (!ok) {
        // ripristina la cella al valore originale senza rilanciare l'evento
        suppressChangeRef.current = true;
        node.setDataValue(field, oldValue);
        suppressChangeRef.current = false;
        return;
      }
      onCellEdit(data.policy_id, field, String(newValue ?? ""), oldValue, node);
      return;
    }

    if (field === "comunicato") {
      // Toggle boolean and force row style refresh
      onCellEdit(data.policy_id, field, String(!!newValue), oldValue, node);
      event.api.redrawRows({ rowNodes: [event.node] });
    } else {
      onCellEdit(data.policy_id, field, String(newValue ?? ""), oldValue, node);
    }
  }, [onCellEdit]);

  const pinnedBottomRowData = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return [{
      policy_id: "__total__",
      // null e non "": la colonna SCA e' numerica e con una stringa vuota
      // AG Grid scriveva "Invalid Number" nella riga dei totali
      sca: null,
      rat: "",
      contraente: `TOTALE (${totals.count} polizze)`,
      ramo: "",
      importo_netto: totals.importo_netto,
      competenze: totals.competenze,
      totale: totals.totale,
      compagnia: "",
      targa: "",
      telefono: "",
      note: "",
      comunicato: false,
      _isTotalRow: true,
    }];
  }, [rows, totals]);

  const getRowStyle = useCallback((params) => {
    if (params.node.rowPinned === "bottom") {
      return { fontWeight: "700", backgroundColor: "#f3f4f6", borderTop: "2px solid #d1d5db" };
    }
    const stile = {};
    // Riga di una variazione in corso d'anno (appendice): viola chiaro e corsivo
    if (params.data?.is_adjustment) {
      stile.backgroundColor = "rgba(124,58,237,0.07)";
      stile.fontStyle = "italic";
    } else if (params.data?.renewed) {
      // v3.9.14 - Polizza GIA' RINNOVATA: verde con banda laterale
      stile.backgroundColor = "#bbf7d0";
      stile.borderLeft = "4px solid #16a34a";
      stile.fontWeight = "600";
    } else if (params.data?.rata_pagata) {
      // v3.9.14 - Rata semestrale GIA' INCASSATA: verde tenue con banda laterale
      stile.backgroundColor = "#d1fae5";
      stile.borderLeft = "4px solid #059669";
    } else if (params.data?.not_renewed) {
      // v3.9.14 - Polizza segnata NON RINNOVATA: rosa con banda laterale
      stile.backgroundColor = "#fecaca";
      stile.borderLeft = "4px solid #dc2626";
    } else if (params.data?.comunicato) {
      // Riga verde chiaro se comunicato = true
      stile.backgroundColor = "#dcfce7";
    }
    // Stacco visivo quando cambia il giorno: le righe sono ordinate per giorno
    // di scadenza, cosi' si vede dove finisce un giorno e comincia il prossimo
    const i = params.node.rowIndex;
    if (typeof i === "number" && i > 0) {
      const precedente = params.api.getDisplayedRowAtIndex(i - 1);
      const giornoPrima = precedente?.data ? String(precedente.data.sca ?? "") : null;
      const giornoOra = String(params.data?.sca ?? "");
      if (giornoPrima !== null && giornoPrima !== giornoOra) {
        stile.borderTop = "2px solid #d1d5db";
      }
    }
    return Object.keys(stile).length ? stile : null;
  }, []);

  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm border rounded-md bg-muted/10" data-testid="portfolio-empty">
        Nessuna polizza in scadenza questo mese
      </div>
    );
  }

  // Altezza grid: se poche righe, si adatta. Se tante, usa quasi tutta la viewport.
  const contentHeight = rows.length * 34 + 80;
  const useFullHeight = contentHeight > 500;

  return (
    <>
      {/* Su telefono una griglia a 12 colonne scorre di lato e nasconde
          proprio le informazioni importanti: meglio schede impilate. */}
      <div className="md:hidden space-y-2" data-testid="portfolio-cards">
        {rows.map((r) => (
          <div
            key={r.policy_id || r.contraente}
            className="rounded-md border bg-card p-3 space-y-1"
            style={
              r.renewed
                ? { backgroundColor: "#bbf7d0", borderLeft: "4px solid #16a34a" }
                : r.rata_pagata
                  ? { backgroundColor: "#d1fae5", borderLeft: "4px solid #059669" }
                  : r.not_renewed
                    ? { backgroundColor: "#fecaca", borderLeft: "4px solid #dc2626" }
                    : undefined
            }
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium">{r.contraente}</span>
              <span className="flex items-center gap-1">
                {r.renewed ? (
                  <span className="text-[10px] font-bold text-green-700 border border-green-600 rounded px-1">RINNOVATA</span>
                ) : null}
                {r.not_renewed ? (
                  <span className="text-[10px] font-bold text-red-700 border border-red-500 rounded px-1">NON RINNOVATA</span>
                ) : null}
                {r.rata_pagata ? (
                  <span className="text-[10px] font-bold text-emerald-700 border border-emerald-600 rounded px-1">RATA PAGATA</span>
                ) : null}
                {r.rat === "S" ? (
                  <span className="text-[10px] font-bold text-amber-600 border border-amber-500 rounded px-1">RATA</span>
                ) : null}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {[r.ramo, r.compagnia].filter(Boolean).join(" \u00b7 ")}
              {r.targa ? ` \u00b7 ${r.targa}` : ""}
            </div>
            {r.sca ? <div className="text-xs text-muted-foreground">Scadenza {r.sca}</div> : null}
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-muted-foreground">Totale</span>
              <span className="font-semibold tabular-nums">{euroFormatter({ value: r.totale })}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block" style={{ height: useFullHeight ? "calc(100vh - 260px)" : contentHeight, minHeight: 300, width: "100%" }} data-testid="portfolio-grid">
      <AgGridReact
        ref={gridRef}
        modules={[AllCommunityModule]}
        theme={gridTheme}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        pinnedBottomRowData={pinnedBottomRowData}
        onCellValueChanged={onCellValueChanged}
        rowSelection={rowSelection}
        onSelectionChanged={onSelectionChanged}
        getRowStyle={getRowStyle}
        singleClickEdit={false}
        stopEditingWhenCellsLoseFocus={true}
        animateRows={false}
        // Annulla/ripristina sulle celle con Ctrl+Z, come in Excel
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={30}
        // Filtro immediato: filtra mentre si scrive nella casella di ricerca
        quickFilterText={filtro}
        suppressColumnVirtualisation={true}
        domLayout="normal"
        getRowId={(params) => params.data._isTotalRow ? "__total__" : (params.data.row_id || params.data.policy_id || params.data.contraente)}
      />
    </div>
    </>
  );
}

function SospensioniGrid({ rows }) {
  const columnDefs = useMemo(() => [
    { headerName: "CONTRAENTE", field: "contraente", minWidth: 180, flex: 1, cellStyle: { fontWeight: "500" } },
    { headerName: "RAMO", field: "ramo", width: 100 },
    { headerName: "COMPAGNIA", field: "compagnia", width: 130 },
    { headerName: "TARGA", field: "targa", width: 110, cellStyle: { fontFamily: "monospace" } },
    { headerName: "IMP. NETTO", field: "importo_netto", width: 110, cellStyle: { textAlign: "right", fontFamily: "monospace" }, valueFormatter: euroFormatter },
    { headerName: "TOTALE", field: "totale", width: 110, cellStyle: { textAlign: "right", fontFamily: "monospace", fontWeight: "600" }, valueFormatter: euroFormatter },
    { headerName: "DATA SOSP.", field: "suspension_date", width: 110, valueFormatter: dataFormatter },
    { headerName: "MOTIVO", field: "suspension_reason", minWidth: 150, flex: 1 },
    { headerName: "NOTE", field: "note", minWidth: 120, flex: 0.5, cellStyle: { color: "#6b7280" } },
  ], []);

  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm border rounded-md bg-muted/10" data-testid="sospensioni-empty">
        Nessuna polizza sospesa
      </div>
    );
  }

  return (
    <>
      <div className="md:hidden space-y-2" data-testid="sospensioni-cards">
        {rows.map((r) => (
          <div key={r.policy_id || r.contraente} className="rounded-md border bg-card p-3 space-y-1">
            <span className="font-medium">{r.contraente}</span>
            <div className="text-xs text-muted-foreground">
              {[r.ramo, r.compagnia].filter(Boolean).join(" \u00b7 ")}
              {r.targa ? ` \u00b7 ${r.targa}` : ""}
            </div>
            {r.suspension_date ? (
              <div className="text-xs text-muted-foreground">Sospesa dal {formatDate(r.suspension_date)}</div>
            ) : null}
            {r.suspension_reason ? (
              <div className="text-xs text-muted-foreground">Motivo: {r.suspension_reason}</div>
            ) : null}
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-muted-foreground">Totale</span>
              <span className="font-semibold tabular-nums">{euroFormatter({ value: r.totale })}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block" style={{ height: Math.min(rows.length * 34 + 60, 500), width: "100%" }} data-testid="sospensioni-grid">
      <AgGridReact
        modules={[AllCommunityModule]}
        theme={gridTheme}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={{ sortable: true, resizable: true }}
        animateRows={false}
        domLayout="normal"
        getRowId={(params) => params.data.policy_id || params.data.contraente}
      />
    </div>
    </>
  );
}

export default function PortfolioPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [activeTab, setActiveTab] = useState(String(new Date().getMonth() + 1));
  // filtro immediato della griglia (cerca cliente, targa, compagnia…) nel mese aperto
  const [filtro, setFiltro] = useState("");
  // guardia condivisa griglia/padre per non rilanciare l'evento durante un rollback
  const suppressChangeRef = useRef(false);
  // v3.9.15 — Annulla/Ripristina e Cronologia
  const conferma = useConferma();
  const gridRef = useRef(null);
  const [rigaSelezionata, setRigaSelezionata] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logItems, setLogItems] = useState([]);
  // riga per cui il pannello e' stato aperto (policy_id): resta ferma mentre e' aperto
  const [logPolicyId, setLogPolicyId] = useState(null);
  const [logContraente, setLogContraente] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`/api/portfolio/?year=${year}`);
      setData(res.data);
    } catch {
      toast.error("Errore caricamento portafoglio");
    } finally {
      setLoading(false);
    }
  }, [year]);

  const fetchYears = useCallback(async () => {
    try {
      const res = await axios.get("/api/portfolio/years");
      if (res.data.years?.length) setYears(res.data.years);
    } catch {}
  }, []);

  useEffect(() => { fetchYears(); }, [fetchYears]);
  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);
  // v3.9.15 — cambiando mese o anno la griglia viene ricreata: la selezione non vale piu'
  useEffect(() => { setRigaSelezionata(null); }, [activeTab, year]);

  const handleCellEdit = useCallback(async (policyId, field, value, oldValue, node) => {
    const isFinancial = field === "importo_netto" || field === "totale";
    try {
      await axios.put("/api/portfolio/inline-edit", {
        policy_id: policyId,
        field,
        value,
      });
      // v3.9.13 — Si ricarica SOLO per i campi economici (la consulenza va
      // ricalcolata). Ricaricare a ogni modifica azzerava la cronologia della
      // griglia, e cosi' l'annulla con Ctrl+Z non funzionava piu' per le
      // modifiche normali (compagnia, targa, note).
      if (isFinancial) {
        fetchData();
        toast.success("Modifica salvata");
      }
    } catch (error) {
      // v3.9.12 — ROLLBACK: la griglia non deve mostrare un valore non salvato
      if (node && oldValue !== undefined) {
        suppressChangeRef.current = true;
        try { node.setDataValue(field, oldValue); } catch {}
        suppressChangeRef.current = false;
      }
      toast.error(error?.response?.data?.detail || "Errore salvataggio: valore ripristinato");
      fetchData();
    }
  }, [fetchData]);

  // v3.9.15 — Annulla/Ripristina: API nativa di AG Grid (la stessa di Ctrl+Z).
  // Se non c'e' niente da annullare/ripristinare la chiamata non fa nulla.
  const handleUndo = useCallback(() => {
    try {
      gridRef.current?.api?.undoCellEditing?.();
    } catch {
      toast.error("Impossibile annullare");
    }
  }, []);

  const handleRedo = useCallback(() => {
    try {
      gridRef.current?.api?.redoCellEditing?.();
    } catch {
      toast.error("Impossibile ripristinare");
    }
  }, []);

  // v3.9.15 — Cronologia modifiche (collezione portfolio_edit_log lato server)
  const fetchEditLog = useCallback(async (policyId) => {
    setLogLoading(true);
    try {
      const params = { limit: 50 };
      if (policyId) params.policy_id = policyId;
      const res = await axios.get("/api/portfolio/edit-log", { params });
      setLogItems(res.data?.items || []);
    } catch {
      toast.error("Errore caricamento cronologia");
      setLogItems([]);
    } finally {
      setLogLoading(false);
    }
  }, []);

  const apriCronologia = useCallback(() => {
    const riga = rigaSelezionata;
    setLogPolicyId(riga?.policy_id || null);
    setLogContraente(riga?.contraente || "");
    setLogOpen(true);
    fetchEditLog(riga?.policy_id || null);
  }, [fetchEditLog, rigaSelezionata]);

  const handleRestore = useCallback(async (voce) => {
    const isFinancial = voce.field === "importo_netto" || voce.field === "totale";
    if (isFinancial) {
      const ok = await conferma({
        titolo: `Ripristinare "${FIELD_LABELS[voce.field] || voce.field}"?`,
        descrizione:
          `Il campo torna a: ${fmtValoreStorico(voce.field, voce.old_value)}\n\n` +
          `Questa modifica tocca un campo economico e viene salvata subito.`,
      });
      if (!ok) return;
    }
    try {
      await axios.post(`/api/portfolio/edit-log/${voce.id}/restore`);
      toast.success("Modifica ripristinata");
      await fetchData();
      await fetchEditLog(logPolicyId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Errore ripristino");
    }
  }, [conferma, fetchData, fetchEditLog, logPolicyId]);

  const handleExport = useCallback(async (format) => {
    try {
      const res = await axios.get(`/api/portfolio/?year=${year}`);
      const d = res.data;
      const headers = ["MESE", "SCA", "RAT", "CONTRAENTE", "RAMO", "IMPORTO NETTO", "COMPETENZE", "TOTALE", "COMPAGNIA", "TARGA", "TELEFONO", "NOTE"];

      // Build rows
      const allRows = [];
      for (let m = 1; m <= 12; m++) {
        const rows = d.months[String(m)] || [];
        for (const r of rows) {
          allRows.push([
            MONTH_FULL[m - 1], r.sca || "", r.rat,
            r.contraente + (r.renewed ? " [RINNOVATA]" : r.not_renewed ? " [NON RINNOVATA]" : r.rata_pagata ? " [RATA PAGATA]" : ""),
            r.ramo,
            r.importo_netto, r.competenze, r.totale, r.compagnia,
            r.targa, r.telefono, (r.note || "").replace(/;/g, ","),
          ]);
        }
        if (rows.length > 0) {
          const t = d.month_totals[String(m)] || {};
          allRows.push([
            MONTH_FULL[m - 1], "", "", `TOTALE (${rows.length})`, "",
            t.importo_netto || 0, t.competenze || 0, t.totale || 0, "", "", "", ""
          ]);
        }
      }

      if (format === "csv") {
        let csv = headers.join(";") + "\n";
        for (const row of allRows) csv += row.join(";") + "\n";
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
        downloadBlob(blob, `Portafoglio_${year}.csv`);
        toast.success("CSV esportato");
      } else if (format === "excel") {
        const XLSX = await import("xlsx");
        const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows]);
        // Larghezza colonne
        ws["!cols"] = [
          { wch: 12 }, { wch: 5 }, { wch: 4 }, { wch: 25 }, { wch: 12 },
          { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 20 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `Portafoglio ${year}`);
        XLSX.writeFile(wb, `Portafoglio_${year}.xlsx`);
        toast.success("Excel esportato");
      } else if (format === "pdf") {
        const { default: jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        doc.setFontSize(14);
        doc.text(`Portafoglio ${year}`, 14, 15);
        doc.setFontSize(9);
        doc.text(`Totale: ${d.grand_total.count} polizze — ${d.grand_total.totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })} EUR`, 14, 22);
        autoTable(doc, {
          head: [headers],
          body: allRows.map(row => row.map((v, i) => (i >= 5 && i <= 7 && typeof v === "number") ? v.toLocaleString("it-IT", { minimumFractionDigits: 2 }) : v)),
          startY: 26,
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [34, 80, 60], fontSize: 7, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          didParseCell: (data) => {
            // Bold totals rows
            if (data.row.raw && String(data.row.raw[3] || "").startsWith("TOTALE")) {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fillColor = [220, 230, 220];
            }
          },
        });
        doc.save(`Portafoglio_${year}.pdf`);
        toast.success("PDF esportato");
      }
    } catch {
      toast.error("Errore export");
    }
  }, [year]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]" data-testid="portfolio-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentMonth = new Date().getMonth() + 1;

  return (
    <div className="p-3 lg:p-4 space-y-3 w-full" data-testid="portfolio-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <PageHeader
          compatto
          titolo={`Portafoglio ${year}`}
          descrizione={
            <>
              {data?.grand_total?.count || 0} polizze — Netto: {(data?.grand_total?.importo_netto || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" })} — Totale: {(data?.grand_total?.totale || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" })}
            </>
          }
          icona={<FileSpreadsheet className="h-5 w-5 text-green-600" />}
          testIdTitolo="portfolio-title"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Cerca nel mese…"
            className="h-8 w-[170px] text-sm"
            data-testid="portfolio-search"
          />
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[90px] h-8 text-sm" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* v3.9.15 — Annulla/Ripristina sempre visibili (da telefono Ctrl+Z non c'e') */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndo}
            className="h-8"
            title="Annulla l'ultima modifica fatta nella griglia"
            data-testid="undo-btn"
          >
            <Undo2 className="h-3.5 w-3.5 mr-1.5" />
            Annulla
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRedo}
            className="h-8"
            title="Ripristina la modifica appena annullata"
            data-testid="redo-btn"
          >
            <Redo2 className="h-3.5 w-3.5 mr-1.5" />
            Ripristina
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={apriCronologia}
            className="h-8"
            title="Cronologia delle modifiche (per polizza se hai selezionato una riga)"
            data-testid="edit-log-btn"
          >
            <History className="h-3.5 w-3.5 mr-1.5" />
            Cronologia
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("excel")} className="h-8" data-testid="export-excel-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} className="h-8" data-testid="export-pdf-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} className="h-8" data-testid="export-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" />
            CSV
          </Button>
          <Button variant="default" size="sm" onClick={handlePrint} className="h-8" data-testid="print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Stampa
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto h-8" data-testid="month-tabs">
            {MONTH_SHORT.map((m, i) => {
              const monthNum = String(i + 1);
              const count = data?.months?.[monthNum]?.length || 0;
              const isCurrent = i + 1 === currentMonth && year === new Date().getFullYear();
              return (
                <TabsTrigger
                  key={monthNum}
                  value={monthNum}
                  className={`text-xs px-2 h-7 relative ${isCurrent ? "font-bold" : ""}`}
                  data-testid={`tab-month-${monthNum}`}
                >
                  {m}
                  {count > 0 && (
                    <span className="ml-1 rounded bg-muted px-1 text-[10px] font-semibold">{count}</span>
                  )}
                  {isCurrent && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-green-500 rounded-full" />
                  )}
                </TabsTrigger>
              );
            })}
            <TabsTrigger value="sospensioni" className="text-xs px-2 h-7" data-testid="tab-sospensioni">
              <PauseCircle className="h-3 w-3 mr-1" />
              Sospese
              {(data?.sospensioni?.length || 0) > 0 && (
                <span className="ml-0.5 text-[9px] text-amber-600">({data.sospensioni.length})</span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Monthly tabs */}
        {Array.from({ length: 12 }, (_, i) => {
          const monthNum = String(i + 1);
          return (
            <TabsContent key={monthNum} value={monthNum} className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">{MONTH_FULL[i]} {year}</span>
                <Badge variant="outline" className="text-[10px]">
                  {data?.month_totals?.[monthNum]?.count || 0} polizze
                  {data?.month_totals?.[monthNum]?.adjustments_count
                    ? ` · ${data.month_totals[monthNum].adjustments_count} variazioni`
                    : ""}
                  {data?.month_totals?.[monthNum]?.count_chiuse
                    ? ` · ${data.month_totals[monthNum].count_chiuse} chiuse`
                    : ""}
                </Badge>
              </div>
              <MonthGrid
                filtro={filtro}
                rows={data?.months?.[monthNum] || []}
                totals={data?.month_totals?.[monthNum] || { count: 0, importo_netto: 0, competenze: 0, totale: 0 }}
                onCellEdit={handleCellEdit}
                suppressRef={suppressChangeRef}
                gridRef={gridRef}
                onRigaSelezionata={setRigaSelezionata}
              />
            </TabsContent>
          );
        })}

        {/* Sospensioni tab */}
        <TabsContent value="sospensioni" className="mt-2">
          <div className="flex items-center gap-2 mb-2">
            <PauseCircle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-muted-foreground">Polizze Sospese</span>
            <Badge variant="outline" className="text-[10px]">{data?.sospensioni?.length || 0}</Badge>
          </div>
          <SospensioniGrid rows={data?.sospensioni || []} />
        </TabsContent>
      </Tabs>

      {/* v3.9.15 — Pannello Cronologia: ultime 50 modifiche (di una polizza se una
          riga e' selezionata, altrimenti di tutte) con "Ripristina" per voce. */}
      <Sheet open={logOpen} onOpenChange={setLogOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="edit-log-panel">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <History className="h-4 w-4" />
              Cronologia modifiche
            </SheetTitle>
            <p className="text-xs text-muted-foreground">
              {logPolicyId
                ? `Solo ${logContraente || logPolicyId}`
                : "Tutte le polizze (seleziona una riga per filtrare su una polizza)"}
            </p>
          </SheetHeader>

          <div className="mt-4 space-y-2">
            {logLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="edit-log-loading">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : logItems.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground" data-testid="edit-log-empty">
                Nessuna modifica registrata
              </div>
            ) : (
              logItems.map((voce) => (
                <div key={voce.id} className="rounded-md border bg-card p-3 space-y-1" data-testid="edit-log-item">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      {FIELD_LABELS[voce.field] || voce.field}
                    </span>
                    <Badge
                      variant={voce.source === "restore" ? "secondary" : "outline"}
                      className="text-[10px]"
                    >
                      {voce.source === "restore" ? "Ripristino" : "Modifica"}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {voce.contraente || voce.policy_id}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="line-through text-muted-foreground">
                      {fmtValoreStorico(voce.field, voce.old_value)}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium">{fmtValoreStorico(voce.field, voce.new_value)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-[11px] text-muted-foreground">
                      {fmtQuandoStorico(voce.created_at)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleRestore(voce)}
                      data-testid={`restore-${voce.id}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Ripristina
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
